import { isGradient, type GradientPaint } from "./gradient";
import { isRecord } from "../document/model";
import type { CharacterStyle, NativeText } from "./text";
export function unsupported(feature: string): never {
  throw new Error(
    `Импорт пока не поддерживает ${feature}. Текущий документ остаётся на холсте.`,
  );
}
export function legacyNumber(
  d: Record<string, unknown>,
  key: string,
  fallback = 0,
) {
  if (d[key] == null) return fallback;
  if (typeof d[key] !== "number" || !Number.isFinite(d[key]))
    return unsupported(`число «${key}»`);
  return d[key];
}
export function legacyColor(value: unknown): { color: string; alpha: number } {
  if (value == null || value === "" || value === "transparent")
    return { color: "#000000", alpha: 0 };
  if (typeof value !== "string") return unsupported("градиент или узор");
  let v = value.toLowerCase();
  v =
    (
      {
        black: "#000000",
        white: "#ffffff",
        red: "#ff0000",
        blue: "#0000ff",
        green: "#008000",
      } as Record<string, string>
    )[v] || v;
  if (/^#[\da-f]{3}$/.test(v))
    v = "#" + [...v.slice(1)].map((c) => c + c).join("");
  if (/^#[\da-f]{6}$/.test(v)) return { color: v, alpha: 1 };
  if (/^#[\da-f]{8}$/.test(v))
    return { color: v.slice(0, 7), alpha: parseInt(v.slice(7), 16) / 255 };
  const m =
    /^rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)(?:\s*,\s*(\d*\.?\d+))?\s*\)$/.exec(
      v,
    );
  if (!m || m.slice(1, 4).some((n) => Number(n) > 255) || Number(m[4] ?? 1) > 1)
    return unsupported(`цвет «${value}»`);
  return {
    color:
      "#" +
      m
        .slice(1, 4)
        .map((n) => Math.round(Number(n)).toString(16).padStart(2, "0"))
        .join(""),
    alpha: Number(m[4] ?? 1),
  };
}
/** Preserve legacy gradient endpoints instead of approximating them by an angle. */
export function legacyPaint(
  value: unknown,
  object: Record<string, unknown>,
): { color: string; alpha: number; gradient?: GradientPaint } {
  if (!isRecord(value)) return legacyColor(value);
  if (
    !["linear", "radial"].includes(String(value.type)) ||
    !isRecord(value.coords) ||
    !Array.isArray(value.colorStops)
  )
    return unsupported("узор или повреждённый градиент");
  const w = legacyNumber(object, "width"),
    h = legacyNumber(object, "height");
  if (w <= 0 || h <= 0) return unsupported("градиент без размеров");
  const units = value.gradientUnits ?? "pixels";
  if (units !== "pixels" && units !== "percentage")
    return unsupported("единицы градиента");
  if (
    value.gradientTransform &&
    JSON.stringify(value.gradientTransform) !== "[1,0,0,1,0,0]"
  )
    return unsupported("дополнительную матрицу градиента");
  if (value.type === "radial" && units === "percentage" && w !== h)
    return unsupported("эллиптический радиальный градиент");
  const c = value.coords,
    px = units === "percentage" ? w : 1,
    py = units === "percentage" ? h : 1;
  const coordinates = {
    x1: (legacyNumber(c, "x1") * px + legacyNumber(value, "offsetX")) / w,
    y1: (legacyNumber(c, "y1") * py + legacyNumber(value, "offsetY")) / h,
    x2: (legacyNumber(c, "x2") * px + legacyNumber(value, "offsetX")) / w,
    y2: (legacyNumber(c, "y2") * py + legacyNumber(value, "offsetY")) / h,
    r1: (legacyNumber(c, "r1") * px) / Math.min(w, h),
    r2: (legacyNumber(c, "r2") * px) / Math.min(w, h),
  };
  const gradient: GradientPaint = {
    type: value.type as GradientPaint["type"],
    angle:
      ((Math.atan2(
        (coordinates.y2 - coordinates.y1) * h,
        (coordinates.x2 - coordinates.x1) * w,
      ) *
        180) /
        Math.PI +
        360) %
      360,
    coordinates,
    stops: value.colorStops
      .map((stop) => {
        if (!isRecord(stop)) return unsupported("точку градиента");
        const color = legacyColor(stop.color),
          opacity = legacyNumber(stop, "opacity", 1);
        if (opacity < 0 || opacity > 1)
          return unsupported("прозрачность точки градиента");
        return {
          offset: legacyNumber(stop, "offset"),
          color: color.color,
          alpha: color.alpha * opacity,
        };
      })
      .sort((a, b) => a.offset - b.offset),
  };
  if (!isGradient(gradient)) return unsupported("настройки градиента");
  return { color: gradient.stops[0]!.color, alpha: 1, gradient };
}
function style(
  d: Record<string, unknown>,
  character = false,
  frame = d,
): Partial<CharacterStyle> {
  if (character)
    for (const key of Object.keys(d))
      if (
        ![
          "fill",
          "fontFamily",
          "fontSize",
          "fontWeight",
          "fontStyle",
          "underline",
          "overline",
          "linethrough",
          "deltaY",
          "textBackgroundColor",
        ].includes(key)
      )
        unsupported(`стиль символа «${key}»`);
  for (const key of [
    "overline",
    "linethrough",
    "deltaY",
    "textBackgroundColor",
  ])
    if (d[key]) unsupported(`оформление текста «${key}»`);
  const result: Partial<CharacterStyle> = {};
  if (d.fill !== undefined) {
    const c = legacyPaint(d.fill, frame);
    if (c.gradient) result.colorGradient = c.gradient;
    result.color = c.color;
    result.colorAlpha = c.alpha;
  }
  if (d.fontFamily !== undefined) result.family = String(d.fontFamily);
  if (d.fontSize !== undefined) result.size = legacyNumber(d, "fontSize");
  if (d.fontWeight !== undefined) {
    if (
      !["normal", "bold", 400, 700, "400", "700"].includes(
        d.fontWeight as string,
      )
    )
      unsupported("насыщенность шрифта");
    result.bold = d.fontWeight === "bold" || Number(d.fontWeight) === 700;
  }
  if (d.fontStyle !== undefined) {
    if (!["normal", "italic"].includes(String(d.fontStyle)))
      unsupported("начертание шрифта");
    result.italic = d.fontStyle === "italic";
  }
  if (d.underline !== undefined) result.underline = d.underline === true;
  return result;
}
export function importText(d: Record<string, unknown>): NativeText {
  if (d.path || (d.direction && d.direction !== "ltr") || d.splitByGrapheme)
    unsupported("направление или контур текста");
  const fill = legacyPaint(d.fill, d),
    stroke = legacyPaint(d.stroke, d),
    size = legacyNumber(d, "fontSize", 40);
  const t: NativeText = {
    value: String(d.text ?? ""),
    frame: d.type === "textbox",
    layoutWidth: 1,
    layoutHeight: 1,
    family: String(d.fontFamily || "Times New Roman"),
    size,
    color: fill.color,
    colorAlpha: fill.alpha,
    ...(fill.gradient ? { colorGradient: fill.gradient } : {}),
    ...(stroke.gradient ? { strokeGradient: stroke.gradient } : {}),
    bold: false,
    italic: false,
    underline: false,
    align: (d.textAlign || "left") as NativeText["align"],
    lineSpacing: legacyNumber(d, "lineHeight", 1.16),
    letterSpacing: (legacyNumber(d, "charSpacing") * size) / 1000,
    styles: {},
    stroke: stroke.color,
    strokeAlpha: stroke.alpha,
    strokeWidth: stroke.alpha ? legacyNumber(d, "strokeWidth", 1) : 0,
    metrics: { lineHeight: 1.13, baseline: 0.87914, kerning: true },
    ...style(d),
  };
  if (Array.isArray(d.styles)) {
    // Fabric's serialized ranges count graphemes, not UTF-16 code units.
    const indices: number[] = [];
    let offset = 0;
    for (const char of t.value) {
      indices.push(offset);
      offset += char.length;
    }
    for (const range of d.styles) {
      if (
        !isRecord(range) ||
        !isRecord(range.style) ||
        !Number.isInteger(range.start) ||
        !Number.isInteger(range.end) ||
        Number(range.start) < 0 ||
        Number(range.end) < Number(range.start) ||
        Number(range.end) > indices.length
      )
        unsupported("диапазон стилей текста");
      const patch = style(range.style, true, d);
      for (let i = Number(range.start); i < Number(range.end); i++)
        t.styles[indices[i]!] = { ...patch };
    }
  } else if (isRecord(d.styles)) {
    let offset = 0;
    t.value.split("\n").forEach((line, index) => {
      const row = d.styles as Record<string, unknown>,
        styles = row[index];
      if (styles != null && !isRecord(styles)) unsupported("стили строки");
      let col = 0;
      for (const char of line) {
        const s = (styles as Record<string, unknown> | undefined)?.[col++];
        if (s) {
          if (!isRecord(s)) unsupported("стиль символа");
          t.styles[offset] = style(s, true, d);
        }
        offset += char.length;
      }
      offset++;
    });
    for (const [lineIndex, row] of Object.entries(d.styles)) {
      const line = t.value.split("\n")[Number(lineIndex)];
      if (
        !/^\d+$/.test(lineIndex) ||
        line === undefined ||
        !isRecord(row) ||
        Object.keys(row).some(
          (k) => !/^\d+$/.test(k) || Number(k) >= [...line].length,
        )
      )
        unsupported("границы стилей строки");
    }
  } else if (d.styles != null) unsupported("стили текста");
  return t;
}
